import { EntityMapping } from '@/types';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/utils/cn';
import {
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  ExternalLink,
} from 'lucide-react';

interface EntityMappingTableProps {
  mappings: EntityMapping[];
  onResolveConflict?: (
    mappingId: string,
    resolution: 'use_highlevel' | 'use_xero'
  ) => void;
}

export function EntityMappingTable({
  mappings,
  onResolveConflict,
}: EntityMappingTableProps) {
  if (mappings.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-sm text-gray-500">No entity mappings found</p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
              >
                Status
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
              >
                Entity Type
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
              >
                CRM ID
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
              >
                Xero ID
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
              >
                Last Synced
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {mappings.map((mapping) => (
              <tr key={mapping.id} className="hover:bg-gray-50">
                <td className="whitespace-nowrap px-6 py-4">
                  <StatusBadge status={mapping.syncStatus} />
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900 capitalize">
                  {mapping.entityType}
                </td>
                <td className="whitespace-nowrap px-6 py-4">
                  <div className="flex items-center space-x-2">
                    <code className="text-xs text-gray-600">
                      {mapping.highlevelId.substring(0, 12)}...
                    </code>
                    <ExternalLink className="h-3 w-3 text-gray-400" />
                  </div>
                </td>
                <td className="whitespace-nowrap px-6 py-4">
                  <div className="flex items-center space-x-2">
                    <code className="text-xs text-gray-600">
                      {mapping.xeroId.substring(0, 12)}...
                    </code>
                    <ExternalLink className="h-3 w-3 text-gray-400" />
                  </div>
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                  {formatDistanceToNow(new Date(mapping.lastSyncedAt), {
                    addSuffix: true,
                  })}
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                  {mapping.syncStatus === 'conflict' && onResolveConflict && (
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() =>
                          onResolveConflict(mapping.id, 'use_highlevel')
                        }
                        className="text-primary-600 hover:text-primary-900"
                      >
                        Use HL
                      </button>
                      <span className="text-gray-300">|</span>
                      <button
                        onClick={() =>
                          onResolveConflict(mapping.id, 'use_xero')
                        }
                        className="text-xero-blue hover:text-xero-dark"
                      >
                        Use Xero
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatusBadge({
  status,
}: {
  status: 'synced' | 'pending' | 'failed' | 'conflict';
}) {
  const config = {
    synced: {
      icon: CheckCircle,
      text: 'Synced',
      className: 'bg-green-100 text-green-800',
    },
    pending: {
      icon: Clock,
      text: 'Pending',
      className: 'bg-gray-100 text-gray-800',
    },
    failed: {
      icon: XCircle,
      text: 'Failed',
      className: 'bg-red-100 text-red-800',
    },
    conflict: {
      icon: AlertTriangle,
      text: 'Conflict',
      className: 'bg-yellow-100 text-yellow-800',
    },
  };

  const { icon: Icon, text, className } = config[status];

  return (
    <span
      className={cn(
        'inline-flex items-center space-x-1 rounded-full px-2.5 py-0.5 text-xs font-medium',
        className
      )}
    >
      <Icon className="h-3 w-3" />
      <span>{text}</span>
    </span>
  );
}
